import { useEffect, useRef, useState } from 'react'
import { useAccessKeyPlaintext } from '../api'
import { copyToClipboard } from '../lib/clipboard'
import { formatAccessKey } from '../lib/accessKey'
import { TIMEOUTS } from '../config/constants'

interface AccessKeyCellProps {
  userId: string
  maskedKey: string
}

/**
 * "Ключ" table cell — masked by default, with an eye toggle that reveals
 * the full plaintext key inline and a copy button. The plaintext is
 * fetched lazily (see `useAccessKeyPlaintext`) so it leaves the server
 * only on an explicit reveal or copy. Both buttons stop click
 * propagation — the surrounding row opens the edit dialog.
 */
export function AccessKeyCell({ userId, maskedKey }: AccessKeyCellProps) {
  const [revealed, setRevealed] = useState(false)
  const [copied, setCopied] = useState(false)
  const copyResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const mounted = useRef(true)

  const { data, refetch } = useAccessKeyPlaintext(userId, revealed)

  useEffect(() => () => {
    mounted.current = false
    if (copyResetTimer.current !== null) clearTimeout(copyResetTimer.current)
  }, [])

  // Until the fetch lands, fall back to the mask — a 12-char string
  // arrives fast enough that a spinner would only flicker.
  const display = revealed && data ? formatAccessKey(data.key) : maskedKey

  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    let key = data?.key
    if (!key) {
      const result = await refetch()
      key = result.data?.key
    }
    if (key && (await copyToClipboard(key))) {
      // The awaits above can outlive the component — the admin re-filters
      // the table or pages away while the fetch/clipboard write is in
      // flight. Registering the reset timer then leaks it: the unmount
      // cleanup already ran (copyResetTimer.current was still null), so
      // nothing pairs the timeout. Bail before touching state or the timer.
      if (!mounted.current) return
      setCopied(true)
      if (copyResetTimer.current !== null) clearTimeout(copyResetTimer.current)
      copyResetTimer.current = setTimeout(
        () => setCopied(false),
        TIMEOUTS.CLIPBOARD_FEEDBACK_MS,
      )
    }
  }

  return (
    <div className="flex items-center gap-1.5">
      <span className="font-mono text-sm text-gray-600 dark:text-gray-300">
        {display}
      </span>

      <button
        onClick={e => { e.stopPropagation(); setRevealed(v => !v) }}
        title={revealed ? 'Скрыть ключ' : 'Показать ключ'}
        aria-label={revealed ? 'Скрыть ключ' : 'Показать ключ'}
        aria-pressed={revealed}
        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        {revealed ? (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>

      <button
        onClick={copy}
        title="Скопировать ключ"
        aria-label="Скопировать ключ"
        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
      >
        {copied ? (
          <span className="text-xs font-medium text-green-500">Скопировано</span>
        ) : (
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
          </svg>
        )}
      </button>
    </div>
  )
}
